import React, { useCallback } from 'react';

import { defineMessages, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { Text } from '@atlaskit/primitives/compiled';

import type { UpdateWorkItemAllFieldTypesAction } from '../../../../../common/types';
import { FieldTooltipContent } from '../../../../../common/ui/FieldTooltipContent';
import {
	type RegisterTooltipProvider,
	useRegisterTooltipContentProvider,
} from '../../../../../common/utils/useRegisterTooltipContentProvider';
import { getActionData } from '../../../utils';

import type { richTextField_updateWorkItemAllFieldTypesActionRenderer$key } from './__generated__/richTextField_updateWorkItemAllFieldTypesActionRenderer.graphql';

const messages = defineMessages({
	updateAction: {
		id: 'update-work-item-all-fields-action-renderer.ui.action-card.rich-text-field.update-action',
		defaultMessage: 'Update {fieldName}',
		description: 'Label for the change action in the rich text field',
	},
	clearAction: {
		id: 'update-work-item-all-fields-action-renderer.ui.action-card.rich-text-field.clear-action',
		defaultMessage: 'Clear {fieldName}',
		description: 'Label for the clear action in the rich text field',
	},
});

export type Props = {
	queryRef: richTextField_updateWorkItemAllFieldTypesActionRenderer$key;
	action: UpdateWorkItemAllFieldTypesAction;
	registerTooltipProvider?: RegisterTooltipProvider;
};

export const RichTextField = ({
	queryRef,
	action,
	registerTooltipProvider,
}: Props): React.JSX.Element | null => {
	const { formatMessage } = useIntl();

	const { fieldKey, fieldValue, fieldReasoning } = getActionData(action);

	const data = useFragment(
		graphql`
			fragment richTextField_updateWorkItemAllFieldTypesActionRenderer on JiraRichTextField {
				fieldId
				name
				richText {
					adfValue {
						convertedPlainText {
							plainText
							isTruncated
						}
					}
				}
			}
		`,
		queryRef,
	);

	const isFieldRenderable = data?.fieldId === fieldKey;

	const isUpdatingAction = Boolean(fieldValue);

	const toolTipContentProvider = useCallback(() => {
		if (!isFieldRenderable) {
			return null;
		}

		const currentPlainText = data?.richText?.adfValue?.convertedPlainText?.plainText;
		const isTruncated = data?.richText?.adfValue?.convertedPlainText?.isTruncated;

		const current = currentPlainText ? `${currentPlainText}${isTruncated ? '...' : ''}` : undefined;

		const suggested =
			fieldValue !== undefined && fieldValue !== null ? String(fieldValue) : undefined;

		return (
			<FieldTooltipContent
				reasoning={fieldReasoning}
				current={current}
				suggested={suggested}
				fieldName={data?.name}
			/>
		);
	}, [
		fieldValue,
		fieldReasoning,
		data?.name,
		data?.richText?.adfValue?.convertedPlainText?.isTruncated,
		data?.richText?.adfValue?.convertedPlainText?.plainText,
		isFieldRenderable,
	]);

	useRegisterTooltipContentProvider({
		registerTooltipProvider,
		fieldId: fieldKey,
		provider: toolTipContentProvider,
		enabled: isFieldRenderable,
	});

	if (!isFieldRenderable) {
		return null;
	}

	return (
		<Text
			color="color.text.subtle"
			maxLines={1}
			testId="update-work-item-all-fields-action-renderer.ui.action-card.card-fields-list.rich-text-field"
		>
			{formatMessage(isUpdatingAction ? messages.updateAction : messages.clearAction, {
				fieldName: data?.name?.toLowerCase(),
			})}
		</Text>
	);
};
